import React, {
  useCallback,
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useMemo,
} from "react";

import { showNotification } from "@/utils/network";
import { useTranslation } from "react-i18next";
import { NotificationI } from "@/types/notifications";
import {
  getNotifications,
  readAllNotifications,
  readNotifications,
} from "@/api/notifications";
import { useAuthStore } from "@/store/auth";
import { groupNotifications } from "@/utils/notificationGroups";

interface NotificationsDataContextProps {
  notificationsData: NotificationI[];
  unreadCount: number;
  reloadNotifications: () => Promise<void>;
  markNotificationsAsRead: (ids: number[]) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
}

export const NotificationsDataContext = createContext<
  NotificationsDataContextProps | undefined
>(undefined);

interface NotificationsDataProviderProps {
  children: ReactNode;
}

export const NotificationsDataProvider: React.FC<NotificationsDataProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const accessToken = useAuthStore((s) => s.tokenResponse?.access ?? null);
  const [notificationsData, setNotificationsData] = useState<NotificationI[]>(
    []
  );

  const loadNotifications = useCallback(async () => {
    if (!accessToken) {
      setNotificationsData([]);
      return;
    }

    const requestedWithToken = accessToken;
    try {
      const res = await getNotifications();
      // Ignore a response from a request that was still in flight during logout
      // or an account switch.
      if (useAuthStore.getState().getAccessToken() === requestedWithToken) {
        setNotificationsData(res);
      }
    } catch {
      // Notification refresh is background work. The global API interceptor
      // handles expired credentials; an expected 401 during logout must not
      // show an error or navigate away from the page.
      if (!useAuthStore.getState().getAccessToken()) {
        setNotificationsData([]);
      }
    }
  }, [accessToken]);

  const markNotificationsAsRead = useCallback(
    async (ids: number[]) => {
      const uniqueIds = Array.from(new Set(ids));
      if (!uniqueIds.length) return;

      try {
        await readNotifications({ ids: uniqueIds });
        const readIds = new Set(uniqueIds);
        setNotificationsData((current) =>
          current.map((notification) =>
            readIds.has(notification.id)
              ? { ...notification, read: true }
              : notification
          )
        );
      } catch (e) {
        showNotification({
          text: t("errors.generic"),
          type: "error",
        });
      }
    },
    [t]
  );

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await readAllNotifications();
      setNotificationsData((current) =>
        current.map((notification) => ({ ...notification, read: true }))
      );
    } catch (e) {
      showNotification({
        text: t("errors.generic"),
        type: "error",
      });
    }
  }, [t]);

  const unreadCount = useMemo(
    () =>
      groupNotifications(notificationsData).filter(
        (notification) => !notification.read
      ).length,
    [notificationsData]
  );

  useEffect(() => {
    void loadNotifications();

    if (!accessToken) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") {
        void loadNotifications();
      }
    };
    const interval = window.setInterval(refreshIfVisible, 30_000);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [accessToken, loadNotifications]);

  return (
    <NotificationsDataContext.Provider
      value={{
        notificationsData,
        unreadCount,
        reloadNotifications: loadNotifications,
        markNotificationsAsRead,
        markAllNotificationsAsRead,
      }}
    >
      {children}
    </NotificationsDataContext.Provider>
  );
};

export const useNotificationsDataContext = () => {
  const context = useContext(NotificationsDataContext);
  if (!context) {
    throw new Error(
      "useNotificationsDataContext must be used within a NotificationsDataProvider"
    );
  }
  return context;
};
